// 색 — 색 혼합(두 물감을 섞은 색 고르기)과 색 구분(조금 다른 색 하나 찾기).
// 물감 모델: 빨강·노랑·파랑(RYB) 비율 + 흰색·검정 비율. 빨강+노랑=주황, 노랑+파랑=초록, 빨강+파랑=보라, 흰색을 섞으면 연해진다.
// 보기끼리의 색 차이(OKLab 거리)를 레벨이 오를수록 줄여 더 섬세하게 구별하게 한다.
import type { ChoiceQuestion } from './ChoiceDrill';
import { int, makeRng, pick, shuffle, type Rng } from './rng';

export interface Paint { r: number; y: number; b: number; w: number; k: number }
export type RGB = [number, number, number];
export type ColorQuestion = ChoiceQuestion & { swatches: RGB[] } & (
  | { kind: 'mix'; a: RGB; b: RGB; ratio: [number, number] }
  | { kind: 'odd' }
);

export const COLOR_COUNT = 12;

// 빨강·노랑·파랑 비율 → 화면 색 (Gossett & Chen 의 RYB 큐브 꼭짓점 보간)
const CORNERS: Record<string, RGB> = {
  '000': [1, 1, 1], '100': [1, 0, 0], '010': [1, 1, 0], '001': [0.163, 0.373, 0.6],
  '110': [1, 0.5, 0], '101': [0.5, 0, 0.5], '011': [0, 0.66, 0.2], '111': [0.2, 0.094, 0],
};
function ryb(r: number, y: number, b: number): RGB {
  const out: RGB = [0, 0, 0];
  for (const [key, c] of Object.entries(CORNERS)) {
    const w = (key[0] === '1' ? r : 1 - r) * (key[1] === '1' ? y : 1 - y) * (key[2] === '1' ? b : 1 - b);
    for (let i = 0; i < 3; i++) out[i] += c[i] * w;
  }
  return out;
}

export function paintRgb(p: Paint): RGB {
  const chroma = p.r + p.y + p.b, total = chroma + p.w + p.k;
  const m = Math.max(p.r, p.y, p.b);
  const base: RGB = chroma > 0 ? ryb(p.r / m, p.y / m, p.b / m) : [0.5, 0.5, 0.5];
  return base.map((v) => (v * chroma + p.w + 0.08 * p.k) / total) as RGB;
}

export function mixPaint(a: Paint, b: Paint, wa = 1, wb = 1): Paint {
  const t = wa + wb;
  return { r: (a.r * wa + b.r * wb) / t, y: (a.y * wa + b.y * wb) / t, b: (a.b * wa + b.b * wb) / t, w: (a.w * wa + b.w * wb) / t, k: (a.k * wa + b.k * wb) / t };
}

// ---- OKLab (색 차이 계산) ----
const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const gam = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);
export function oklab([R, G, B]: RGB): RGB {
  const r = lin(R), g = lin(G), b = lin(B);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}
function fromOklab([L, A, Bb]: RGB): RGB {
  const l = (L + 0.3963377774 * A + 0.2158037573 * Bb) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * Bb) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * Bb) ** 3;
  const rgb = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
  return rgb.map((c) => Math.min(1, Math.max(0, gam(Math.min(1, Math.max(0, c)))))) as RGB;
}
/** 색 차이 (OKLab 거리 × 100 — 2쯤이 겨우 구별되는 차이) */
export const deltaE = (a: RGB, b: RGB) => {
  const p = oklab(a), q = oklab(b);
  return 100 * Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
};
export const css = (c: RGB) => `rgb(${c.map((v) => Math.round(v * 255)).join(' ')})`;

const PURE: Paint[] = [
  { r: 1, y: 0, b: 0, w: 0, k: 0 }, { r: 0, y: 1, b: 0, w: 0, k: 0 }, { r: 0, y: 0, b: 1, w: 0, k: 0 },
];
function randomPaint(r: Rng, lv: number): Paint {
  if (lv <= 3) return r() < 0.8 ? pick(r, PURE) : { r: 0, y: 0, b: 0, w: 1, k: 0 };
  const p: Paint = { r: 0, y: 0, b: 0, w: 0, k: 0 };
  const [c1, c2] = shuffle(r, ['r', 'y', 'b'] as const);
  p[c1] = 1;
  if (r() < (lv >= 7 ? 0.6 : 0.3)) p[c2] = pick(r, [0.3, 0.5, 1]);
  if (r() < 0.35) p.w = pick(r, [0.5, 1, 2]);
  else if (lv >= 6 && r() < 0.2) p.k = pick(r, [0.2, 0.4]);
  return p;
}

/** 정답과 충분히 다르고 서로도 다른 보기 3개 */
function pickDistinct(answer: RGB, cands: RGB[], sep: number): RGB[] | null {
  const chosen: RGB[] = [];
  for (const c of cands) {
    if (deltaE(c, answer) < sep || chosen.some((x) => deltaE(x, c) < sep * 0.7)) continue;
    chosen.push(c);
    if (chosen.length === 3) return chosen;
  }
  return null;
}

function mixQ(r: Rng, lv: number): ColorQuestion | null {
  const A = randomPaint(r, lv), B = randomPaint(r, lv);
  const a = paintRgb(A), b = paintRgb(B);
  if (deltaE(a, b) < 20) return null; // 같은 색끼리 섞기는 문제가 안 된다
  const ratio: [number, number] = lv >= 7 ? pick(r, [[1, 1], [2, 1], [1, 2]]) : [1, 1];
  const ans = paintRgb(mixPaint(A, B, ratio[0], ratio[1]));
  const white: Paint = { r: 0, y: 0, b: 0, w: 1, k: 0 }, black: Paint = { r: 0, y: 0, b: 0, w: 0, k: 1 };
  const third = pick(r, PURE);
  const cands = shuffle(r, [
    paintRgb(mixPaint(A, B, ratio[0] * 2, ratio[1])), paintRgb(mixPaint(A, B, ratio[0], ratio[1] * 2)),
    paintRgb(mixPaint(mixPaint(A, B, ratio[0], ratio[1]), white, 2, 1)), paintRgb(mixPaint(mixPaint(A, B, ratio[0], ratio[1]), black, 4, 1)),
    paintRgb(mixPaint(mixPaint(A, B, ratio[0], ratio[1]), third, 3, 1)), paintRgb(mixPaint(A, third)), paintRgb(mixPaint(B, third)), a, b,
  ]);
  const sep = 16 - lv * 1.1;
  const wrong = pickDistinct(ans, cands, sep);
  if (!wrong) return null;
  const swatches = shuffle(r, [ans, ...wrong]);
  return { kind: 'mix', a, b, ratio, swatches, answer: swatches.indexOf(ans), n: 4, limitSec: Math.max(12, 26 - lv), explain: '물감을 섞으면 빨강+노랑 → 주황, 노랑+파랑 → 초록, 빨강+파랑 → 보라. 흰색은 연하게, 검정은 어둡게, 많이 넣은 쪽 색에 가까워진다.' };
}

function oddQ(r: Rng, lv: number): ColorQuestion | null {
  const base = paintRgb(randomPaint(r, Math.max(4, lv)));
  const target = Math.max(3.2, 13 - lv * 1.0);
  const lab = oklab(base);
  const dir = shuffle(r, [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]])[0];
  const odd = fromOklab([lab[0] + (dir[0] * target) / 100, lab[1] + (dir[1] * target) / 100, lab[2] + (dir[2] * target) / 100]);
  const d = deltaE(base, odd);
  if (d < target * 0.75 || d > target * 1.3) return null; // 화면에 낼 수 없는 색(범위 밖)이면 다시
  const at = int(r, 0, 8);
  const swatches = Array.from({ length: 9 }, (_, i) => (i === at ? odd : base));
  return { kind: 'odd', swatches, answer: at, n: 9, limitSec: Math.max(7, 16 - lv), explain: `차이 ${d.toFixed(1)} (2쯤이 겨우 보이는 차이). 요령: 한 칸만 오래 보지 말고 전체를 한 번에 훑어 튀는 칸을 찾는다.` };
}

export function makeColorRound(level: number, seed: number): ColorQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const out: ColorQuestion[] = [];
  for (let k = 0; k < COLOR_COUNT; k++) {
    for (let guard = 0; guard < 200; guard++) {
      const q = k % 2 === 0 ? mixQ(rng, lv) : oddQ(rng, lv);
      if (q) {
        out.push(q);
        break;
      }
    }
  }
  return out;
}
